// src/components/common/FilterBar.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';

const FilterBar = ({ filters, onFilterChange, initialValues = {} }) => {
  const [filterValues, setFilterValues] = useState(initialValues);
  const [isExpanded, setIsExpanded] = useState(false);
  const initialMount = useRef(true);

  const styles = {
    container: {
      backgroundColor: '#ffffff',
      borderRadius: '0.5rem',
      border: '1px solid #e5e7eb',
      overflow: 'hidden',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '0.75rem 1rem',
      backgroundColor: '#f9fafb',
      borderBottom: '1px solid #e5e7eb',
      cursor: 'pointer',
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    headerTitle: {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: '#374151',
    },
    filterCount: {
      padding: '0.125rem 0.5rem',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      fontSize: '0.75rem',
      fontWeight: 500,
      borderRadius: '9999px',
    },
    expandIcon: {
      width: '1.25rem',
      height: '1.25rem',
      color: '#6b7280',
      transition: 'transform 0.2s',
    },
    content: {
      padding: isExpanded ? '1rem' : '0',
      maxHeight: isExpanded ? '1000px' : '0',
      overflow: 'hidden',
      transition: 'all 0.3s ease-in-out',
    },
    filterGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
      gap: '1rem',
      marginBottom: '1rem',
    },
    filterGroup: {
      display: 'flex',
      flexDirection: 'column',
    },
    label: {
      fontSize: '0.75rem',
      fontWeight: 500,
      color: '#6b7280',
      marginBottom: '0.25rem',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
    },
    input: {
      width: '100%',
      padding: '0.5rem 0.75rem',
      fontSize: '0.875rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      transition: 'border-color 0.2s, boxShadow 0.2s',
    },
    inputFocus: {
      borderColor: '#2563eb',
      boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.1)',
    },
    select: {
      width: '100%',
      padding: '0.5rem 0.75rem',
      fontSize: '0.875rem',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      outline: 'none',
      backgroundColor: '#ffffff',
      cursor: 'pointer',
    },
    checkbox: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      cursor: 'pointer',
    },
    checkboxInput: {
      width: '1rem',
      height: '1rem',
      cursor: 'pointer',
    },
    checkboxLabel: {
      fontSize: '0.875rem',
      color: '#374151',
      cursor: 'pointer',
    },
    radioGroup: {
      display: 'flex',
      gap: '1rem',
      flexWrap: 'wrap',
    },
    radio: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem',
      cursor: 'pointer',
    },
    radioInput: {
      width: '1rem',
      height: '1rem',
      cursor: 'pointer',
    },
    radioLabel: {
      fontSize: '0.875rem',
      color: '#374151',
      cursor: 'pointer',
    },
    dateRange: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '0.5rem',
    },
    actions: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '0.75rem',
      marginTop: '1rem',
      paddingTop: '1rem',
      borderTop: '1px solid #e5e7eb',
    },
    applyButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#2563eb',
      color: '#ffffff',
      border: 'none',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'background-color 0.2s',
    },
    applyButtonHover: {
      backgroundColor: '#1d4ed8',
    },
    resetButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#ffffff',
      color: '#374151',
      border: '1px solid #d1d5db',
      borderRadius: '0.375rem',
      fontSize: '0.875rem',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    resetButtonHover: {
      backgroundColor: '#f9fafb',
    },
    activeFilters: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.5rem',
      marginTop: '0.5rem',
      padding: '0.5rem 1rem',
      backgroundColor: '#f3f4f6',
      borderRadius: '0.375rem',
    },
    filterTag: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.25rem',
      padding: '0.25rem 0.75rem',
      backgroundColor: '#ffffff',
      border: '1px solid #e5e7eb',
      borderRadius: '9999px',
      fontSize: '0.75rem',
      color: '#374151',
    },
    removeTag: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '1rem',
      height: '1rem',
      marginLeft: '0.25rem',
      border: 'none',
      background: 'none',
      borderRadius: '50%',
      color: '#9ca3af',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    removeTagHover: {
      backgroundColor: '#fee2e2',
      color: '#dc2626',
    },
  };

  // Count active filters
  const getActiveFilterCount = useCallback(() => {
    return Object.entries(filterValues).filter(([key, value]) => {
      if (value === undefined || value === null || value === '') return false;
      if (Array.isArray(value) && value.length === 0) return false;
      return true;
    }).length;
  }, [filterValues]);

  const activeFilterCount = getActiveFilterCount();

  const handleFilterChange = (name, value) => {
    setFilterValues((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleApplyFilters = () => {
    if (onFilterChange) {
      onFilterChange(filterValues);
    }
  };

  const handleResetFilters = () => {
    const resetValues = {};
    filters.forEach((filter) => {
      if (filter.type === 'checkbox') {
        resetValues[filter.name] = filter.defaultValue || false;
      } else if (filter.type === 'radio') {
        resetValues[filter.name] = filter.defaultValue || '';
      } else if (filter.type === 'dateRange') {
        resetValues[filter.name] = { start: '', end: '' };
      } else {
        resetValues[filter.name] = filter.defaultValue || '';
      }
    });
    setFilterValues(resetValues);
    
    if (onFilterChange) {
      onFilterChange(resetValues);
    }
  };

  const handleRemoveFilter = (name) => {
    const filter = filters.find(f => f.name === name);
    let defaultValue = '';
    
    if (filter) {
      if (filter.type === 'checkbox') {
        defaultValue = filter.defaultValue || false;
      } else if (filter.type === 'dateRange') {
        defaultValue = { start: '', end: '' };
      }
    }
    
    setFilterValues((prev) => ({
      ...prev,
      [name]: defaultValue,
    }));

    // Apply after removal
    setTimeout(() => {
      if (onFilterChange) {
        onFilterChange({
          ...filterValues,
          [name]: defaultValue,
        });
      }
    }, 0);
  };

  // Get display value for filter tag
  const getFilterDisplayValue = (filter, value) => {
    if (value === undefined || value === null || value === '') return null;
    
    if (filter.type === 'select' && filter.options) {
      const option = filter.options.find(opt => opt.value === value);
      return option ? option.label : value;
    }
    
    if (filter.type === 'checkbox') {
      return value ? 'Oui' : 'Non';
    }
    
    if (filter.type === 'dateRange') {
      if (value.start && value.end) {
        return `${value.start} - ${value.end}`;
      } else if (value.start) {
        return `À partir du ${value.start}`;
      } else if (value.end) {
        return `Jusqu'au ${value.end}`;
      }
      return null;
    }
    
    return value;
  };

  // Update filterValues when initialValues change, but only on mount or when initialValues actually change
  useEffect(() => {
    // Only update if this is the initial mount or if initialValues have actually changed
    if (initialMount.current) {
      initialMount.current = false;
      setFilterValues(initialValues);
    } else {
      // Check if initialValues are different from current filterValues
      const hasChanges = Object.keys(initialValues).some(
        key => JSON.stringify(initialValues[key]) !== JSON.stringify(filterValues[key])
      );
      
      if (hasChanges) {
        setFilterValues(initialValues);
      }
    }
  }, [initialValues, filterValues]);

  // Render filter based on type
  const renderFilter = (filter) => {
    const value = filterValues[filter.name] !== undefined 
      ? filterValues[filter.name] 
      : filter.defaultValue || '';

    switch (filter.type) {
      case 'text':
        return (
          <input
            type="text"
            id={filter.name}
            name={filter.name}
            value={value}
            onChange={(e) => handleFilterChange(filter.name, e.target.value)}
            placeholder={filter.placeholder || `Filtrer par ${filter.label.toLowerCase()}...`}
            style={styles.input}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = styles.inputFocus.borderColor;
              e.currentTarget.style.boxShadow = styles.inputFocus.boxShadow;
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = styles.input.borderColor;
              e.currentTarget.style.boxShadow = 'none';
            }}
          />
        );

      case 'select':
        return (
          <select
            id={filter.name}
            name={filter.name}
            value={value}
            onChange={(e) => handleFilterChange(filter.name, e.target.value)}
            style={styles.select}
          >
            {filter.options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case 'checkbox':
        return (
          <label style={styles.checkbox}>
            <input
              type="checkbox"
              name={filter.name}
              checked={value}
              onChange={(e) => handleFilterChange(filter.name, e.target.checked)}
              style={styles.checkboxInput}
            />
            <span style={styles.checkboxLabel}>{filter.label}</span>
          </label>
        );

      case 'radio':
        return (
          <div style={styles.radioGroup}>
            {filter.options.map((option) => (
              <label key={option.value} style={styles.radio}>
                <input
                  type="radio"
                  name={filter.name}
                  value={option.value}
                  checked={value === option.value}
                  onChange={(e) => handleFilterChange(filter.name, e.target.value)}
                  style={styles.radioInput}
                />
                <span style={styles.radioLabel}>{option.label}</span>
              </label>
            ))}
          </div>
        );

      case 'dateRange':
        return (
          <div style={styles.dateRange}>
            <input
              type="date"
              value={value.start || ''}
              onChange={(e) => handleFilterChange(filter.name, { ...value, start: e.target.value })}
              style={styles.input}
              placeholder="Date début"
            />
            <input
              type="date"
              value={value.end || ''}
              onChange={(e) => handleFilterChange(filter.name, { ...value, end: e.target.value })}
              style={styles.input}
              placeholder="Date fin"
            />
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div
        style={styles.header}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div style={styles.headerLeft}>
          <svg
            style={styles.expandIcon}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
            />
          </svg>
          <span style={styles.headerTitle}>Filtres</span>
          {activeFilterCount > 0 && (
            <span style={styles.filterCount}>{activeFilterCount}</span>
          )}
        </div>
        <svg
          style={{
            ...styles.expandIcon,
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </div>

      {/* Content */}
      <div style={styles.content}>
        <div style={styles.filterGrid}>
          {filters.map((filter) => (
            <div key={filter.name} style={styles.filterGroup}>
              {filter.type !== 'checkbox' && (
                <label style={styles.label} htmlFor={filter.name}>
                  {filter.label}
                </label>
              )}
              {renderFilter(filter)}
            </div>
          ))}
        </div>

        <div style={styles.actions}>
          <button
            onClick={handleResetFilters}
            style={styles.resetButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.resetButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.resetButton.backgroundColor;
            }}
          >
            Réinitialiser
          </button>
          <button
            onClick={handleApplyFilters}
            style={styles.applyButton}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = styles.applyButtonHover.backgroundColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = styles.applyButton.backgroundColor;
            }}
          >
            Appliquer les filtres
          </button>
        </div>
      </div>

      {/* Active Filters Tags */}
      {activeFilterCount > 0 && !isExpanded && (
        <div style={styles.activeFilters}>
          {filters.map((filter) => {
            const value = filterValues[filter.name];
            const displayValue = getFilterDisplayValue(filter, value);
            
            if (!displayValue) return null;
            
            return (
              <div key={filter.name} style={styles.filterTag}>
                <span>
                  <strong>{filter.label}:</strong> {displayValue}
                </span>
                <button
                  onClick={() => handleRemoveFilter(filter.name)}
                  style={styles.removeTag}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = styles.removeTagHover.backgroundColor;
                    e.currentTarget.style.color = styles.removeTagHover.color;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.color = styles.removeTag.color;
                  }}
                >
                  <svg
                    style={{ width: '0.875rem', height: '0.875rem' }}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FilterBar;